import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useGoogleLogin } from '@react-oauth/google';
import './Login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { login, googleLogin } = useAuth();
  const navigate = useNavigate();

  // ── Manual Login ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!email.trim()) { setError('Please enter your email.'); return; }
    if (!password) { setError('Please enter your password.'); return; }

    try {
      setLoading(true);
      setError('');
      const result = await login(email.trim(), password, true);
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.message || 'Invalid credentials');
      }
    } catch {
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Google Login (using implicit flow → get access_token → backend verifies) ──
  const handleGoogleSuccess = async (tokenResponse) => {
    try {
      setGoogleLoading(true);
      setError('');
      // tokenResponse.access_token se user info fetch karenge
      // Backend ko credential send karenge
      const result = await googleLogin(tokenResponse.access_token);
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.message || 'Google login failed. Your account may not have admin access.');
      }
    } catch {
      setError('Google login failed. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  const googleLoginTrigger = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: () => {
      setError('Google sign-in was cancelled or failed. Please try again.');
      setGoogleLoading(false);
    },
    flow: 'implicit',
  });

  const handleGoogleClick = () => {
    if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
      setError('Google Sign-In is not configured. Please sign in with your email and password.');
      return;
    }
    setError('');
    setGoogleLoading(true);
    googleLoginTrigger();
  };

  return (
    <div className="lp-root">
      <div className="lp-orb1" />
      <div className="lp-orb2" />

      <div className="lp-card">
        {/* ── Header ── */}
        <div className="lp-header">
          <div className="lp-ftu-logo-wrap">
            <img src="/FTU001.png" alt="FossTechUzon" className="lp-ftu-logo-img"
              onError={(e) => { e.target.style.display = 'none'; }} />
          </div>
          <div className="lp-poweredby">Powered by <span>FossTechUzon</span></div>
          <div className="lp-brand">
            <h1>FossTrack</h1>
            <p>Super Admin Portal</p>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="lp-body">
          <div className="lp-tabs">
            <button className="lp-tab active">
              <span className="lp-tab-icon">🔐</span> Login
            </button>
          </div>

          <div className="lp-tab-content">
            {error && (
              <div className="lp-error">
                <span>⚠️</span>
                <span>{error}</span>
              </div>
            )}

            {/* ── Google Login Button ── */}
            <button
              id="google-login-btn"
              type="button"
              className="lp-google-btn"
              onClick={handleGoogleClick}
              disabled={googleLoading || loading}
            >
              {googleLoading ? (
                <>
                  <div className="lp-spinner" />
                  <span>Signing in with Google...</span>
                </>
              ) : (
                <>
                  <svg className="lp-google-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                  </svg>
                  <span>Continue with Google</span>
                </>
              )}
            </button>

            {/* ── Divider ── */}
            <div className="lp-divider">
              <span className="lp-divider-line" />
              <span className="lp-divider-text">or Login with Google</span>
              <span className="lp-divider-line" />
            </div>

            {/* ── Manual Email/Password Form ── */}
            <form onSubmit={handleSubmit} id="manual-login-form">
              <div className="lp-group">
                <label className="lp-label">Email or Username</label>
                <div className="lp-input-wrap">
                  <span className="lp-input-icon">👤</span>
                  <input
                    id="login-email"
                    type="text"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }}
                    className="lp-input"
                    placeholder="admin@fosstech.com"
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="lp-group">
                <label className="lp-label">Password</label>
                <div className="lp-input-wrap">
                  <span className="lp-input-icon">🔒</span>
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); if (error) setError(''); }}
                    className="lp-input"
                    placeholder="Enter your password"
                    style={{ paddingRight: '42px' }}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="lp-pw-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>

              <button id="login-submit-btn" type="submit" disabled={loading || googleLoading} className="lp-btn">
                {loading ? (
                  <><div className="lp-spinner lp-spinner-white" /> Signing in...</>
                ) : (
                  <><span>🚀</span> Sign In</>
                )}
              </button>
            </form>

            <div className="lp-contact">
              Need access? Contact{' '}
              <a href="mailto:info@fosstechuzon.com" className="lp-link">
                info@fosstechuzon.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
